import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  LinearProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO.tsx';

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
    background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
  },
}));

const skills = [
  { name: 'Java', level: 95 },
  { name: 'Spring Framework', level: 90 },
  { name: 'Spring Boot', level: 90 },
  { name: 'Spring Data', level: 85 },
  { name: 'Spring JDBC', level: 85 },
  { name: 'Microservices', level: 85 },
  { name: 'PostgreSQL', level: 80 },
  { name: 'Docker', level: 80 },
  { name: 'Git', level: 90 },
  { name: 'HTML/CSS', level: 80 },
  { name: 'Thymeleaf', level: 75 },
  { name: 'Spring Security', level: 80 },
  { name: 'JWT', level: 75 },
  { name: 'Maven', level: 85 },
  { name: 'Gradle', level: 80 },
];

const experiences = [
  {
    period: 'Nov 2023 - Present',
    title: 'Java Developer',
    company: 'Freelance / Self-Employed',
    description: 'Development and maintenance of web applications using Java and Spring Framework; Creating RESTful APIs for integration with external services; Consulting on software architecture design; Setting up and deploying applications in Docker containers; Code optimization and refactoring for better performance; Client support and implementation of new features.',
  },
  {
    period: 'Nov 2022 - Jul 2023',
    title: 'Java Programmer',
    company: 'Корпоративный университет Сбербанка (СберУниверситет)',
    description: 'Development of a client-server application using Java 17, Spring Framework (JDBC, JPA, Data, Security, MVC), PostgreSQL, Docker, and frontend technologies including HTML, CSS, Bootstrap, and Thymeleaf.',
  },
  {
    period: 'Feb 2020 - Nov 2021',
    title: 'Java Developer',
    company: 'EPAM Systems',
    description: 'Participation in team development of a server-side application based on a microservices architecture, collaborating with frontend teams; Designing and maintaining microservices; Conducting testing and debugging of code using Spring Boot, Spring Cloud Gateway, RESTful API, and PostgreSQL.',
  },
];

const About = () => {
  useEffect(() => {
    document.title = 'Alexandre Mami | About';
  }, []);

  return (
    <>
      <SEO
        title="About"
        description="Découvrez Alexandre Mami, développeur Full Stack spécialisé dans Java, Spring Framework et React. Expérience en développement d'applications web modernes et performantes."
        keywords="développeur java, spring framework, react, full stack, expérience, compétences, alexandre mami"
        ogImage="https://mamialex.ru/images/about-preview.jpg"
        ogType="article"
      />
      <Box
        sx={{
          minHeight: '100%',
          py: 8,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        }}
      >
        <Container maxWidth="lg">
            <Typography
            variant="h2"
            gutterBottom
            sx={{
              textAlign: 'center',
              mb: 6,
              background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            About Me
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Professional Journey
                  </Typography>
                  <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 4 }}>
                    Java programmer with enthusiasm and experience in web application development. 
                    Specialized in Spring Framework technologies, microservices architecture, and creating 
                    reliable, efficient applications with modern security practices.
                  </Typography>
                  <Stack spacing={4}>
                    {experiences.map((exp, index) => (
                      <Box key={index}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: '#FFC107',
                            mb: 1,
                          }}
                        >
                          {exp.period}
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                          {exp.title}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#6366F1',
                            mb: 1,
                          }}
                        >
                          {exp.company}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {exp.description}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Core Technologies
                  </Typography>
                  <Stack spacing={3}>
                    {skills.map((skill) => (
                      <Box key={skill.name}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body1">{skill.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {skill.level}%
                          </Typography>
                        </Box>
                        <StyledLinearProgress
                          variant="determinate"
                          value={skill.level}
                        />
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default About; 